"use client";

import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getSubjectDisplayName } from "@/lib/data/subjects/subjects";

interface Subject {
    id: number;
    subject: string;
    abbreviation: string | string[];
}

interface SubjectCategory {
    name: string;
    subjects: Subject[];
}

interface ElectiveSelectionDialogProps {
    open: boolean;
    onClose: () => void;
    onApply: () => void;
    tempSelectedElectives: string[];
    maxElectives: number;
    groupedSubjects: SubjectCategory[];
    isElectiveSelected: (subject: Subject) => boolean;
    toggleElective: (subject: Subject) => void;
    isElectiveDisabled: (subject: Subject) => boolean;
    translate: (key: string) => string;
    language: string;
}

export const ElectiveSelectionDialog: React.FC<ElectiveSelectionDialogProps> = ({
    open,
    onClose,
    onApply,
    tempSelectedElectives,
    maxElectives,
    groupedSubjects,
    isElectiveSelected,
    toggleElective,
    isElectiveDisabled,
    translate,
    language,
}) => {
    return (
        <Dialog open={open} onOpenChange={(isOpen) => {
            if (!isOpen) {
                onClose();
            }
        }}>
            <DialogContent className="max-w-[95vw] sm:max-w-4xl max-h-[85vh] sm:max-h-[90vh] overflow-hidden flex flex-col p-0">
                <DialogHeader className="px-4 pt-4 sm:px-6 sm:pt-6 pb-2 sm:pb-4 flex-shrink-0">
                    <DialogTitle className="text-lg sm:text-xl md:text-2xl">{translate('selectYourElectiveSubjects')}</DialogTitle>
                    <DialogDescription className="text-xs sm:text-sm md:text-base">
                        {translate('chooseYourElectives')}
                    </DialogDescription>
                    <Badge variant="outline" className="text-xs sm:text-sm px-2 sm:px-3 py-1 mt-2 w-fit">
                        {tempSelectedElectives.length}/{maxElectives} {translate('electivesSelected')}
                    </Badge>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto px-4 sm:px-6">
                    <div className="space-y-6 sm:space-y-8 py-2 sm:py-4">

                        {(() => {
                            const categoryA = groupedSubjects.filter(c => !c.name.startsWith("Applied Learning") && c.name !== "Languages");
                            const categoryB = groupedSubjects.filter(c => c.name.startsWith("Applied Learning"));
                            const categoryC = groupedSubjects.filter(c => c.name === "Languages");

                            const topLevelCategories = [
                                { id: 'categoryA', categories: categoryA },
                                { id: 'categoryB', categories: categoryB },
                                { id: 'categoryC', categories: categoryC },
                            ];

                            return topLevelCategories.map((topLevel) => (
                                <div key={topLevel.id} className="space-y-3">
                                    <h3 className="text-lg font-bold px-4 py-2 text-blue-900 bg-blue-100 border-y border-blue-200 sticky top-0 z-10">
                                        {translate(topLevel.id as any)}
                                    </h3>
                                    <div className="grid grid-cols-1 gap-4">
                                        {topLevel.categories.map((category, categoryIndex) => (
                                            <Card key={categoryIndex}>
                                                <CardHeader className="py-2 px-3 sm:px-4 md:px-6">

                                                    <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
                                                        <span className="text-lg sm:text-xl md:text-2xl flex-shrink-0">
                                                            {category.name === 'Science' && '🔬'}
                                                            {category.name === 'Mathematics' && '📐'}
                                                            {category.name === 'Society' && '👥'}
                                                            {category.name === 'Arts' && '🎨'}
                                                            {category.name === 'Commerce' && '💼'}
                                                            {category.name === 'Sports' && '⚽'}
                                                            {category.name === 'Applied Learning - Creative Studies' && '🎭'}
                                                            {category.name === 'Applied Learning - Media & Communication' && '📺'}
                                                            {category.name === 'Applied Learning - Business, Management & Law' && '💼'}
                                                            {category.name === 'Applied Learning - Services' && '🍽️'}
                                                            {category.name === 'Applied Learning - Applied Science' && '🧬'}
                                                            {category.name === 'Applied Learning - Engineering & Production' && '⚙️'}
                                                            {category.name === 'Languages' && '🌐'}
                                                        </span>

                                                        <span className="font-semibold break-words text-sm sm:text-base">
                                                            {category.name === 'Science' && translate('scienceCategory')}
                                                            {category.name === 'Mathematics' && translate('mathematicsM1M2')}
                                                            {category.name === 'Society' && translate('societyCategory')}
                                                            {category.name === 'Arts' && translate('artsCategory')}
                                                            {category.name === 'Commerce' && translate('commerce')}
                                                            {category.name === 'Sports' && translate('sportsCategory')}
                                                            {category.name === 'Applied Learning - Creative Studies' && translate('subject_appliedLearningCreativeStudies')}
                                                            {category.name === 'Applied Learning - Media & Communication' && translate('subject_appliedLearningMediaCommunication')}
                                                            {category.name === 'Applied Learning - Business, Management & Law' && translate('subject_appliedLearningBusinessManagementLaw')}
                                                            {category.name === 'Applied Learning - Services' && translate('subject_appliedLearningServices')}
                                                            {category.name === 'Applied Learning - Applied Science' && translate('subject_appliedLearningAppliedScience')}
                                                            {category.name === 'Applied Learning - Engineering & Production' && translate('subject_appliedLearningEngineeringProduction')}
                                                            {category.name === 'Languages' && translate('languagesCategory')}
                                                        </span>
                                                    </CardTitle>
                                                </CardHeader>
                                                <CardContent className="px-3 sm:px-4 md:px-6 pb-3 sm:pb-4 md:pb-6">
                                                    <div className="flex flex-wrap gap-1.5 sm:gap-2">
                                                        {category.subjects.map((subject) => (
                                                            <Button
                                                                key={subject.id}
                                                                variant={isElectiveSelected(subject) ? "default" : "outline"}
                                                                size="sm"
                                                                onClick={() => toggleElective(subject)}
                                                                disabled={isElectiveDisabled(subject)}
                                                                className="text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2 h-auto whitespace-normal text-left min-h-[32px]"
                                                            >
                                                                {getSubjectDisplayName(subject.subject, language)}
                                                            </Button>
                                                        ))}
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </div>
                                </div>
                            ));
                        })()}

                        {tempSelectedElectives.length >= maxElectives && (
                            <div className="text-xs sm:text-sm text-muted-foreground text-center bg-blue-50 p-2 sm:p-3 rounded-lg">
                                {translate('youHaveSelectedMaximum')}
                            </div>
                        )}
                    </div>
                </div>

                <DialogFooter className="px-4 pb-4 sm:px-6 sm:pb-6 pt-2 sm:pt-4 flex-shrink-0 border-t gap-2">
                    <Button
                        onClick={onClose}
                        variant="outline"
                        className="w-full sm:w-auto"
                    >
                        {translate('cancel')}
                    </Button>
                    <Button
                        onClick={onApply}
                        className="w-full sm:w-auto"
                    >
                        {translate('done')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
